import { Injectable, inject } from "@angular/core";
import type { User } from "@angular/fire/auth";
import { BackendApiService, apiErrorMessage } from "@app/common/services/backend-api.service";

export interface PortfolioAccess {
  role: "owner" | "admin" | "viewer";
  canView: boolean;
  canWrite: boolean;
  isPublic: boolean;
}

/**
 * The caller's capability on a portfolio: POST /api/v1/portfolios/:id/access
 * on the backend (slice 2.5; previously a hop through the agent runtime).
 * A portfolio the caller may not see is a 404 and resolves to "no view".
 */
@Injectable({ providedIn: "root" })
export class PortfolioAccessService {
  private readonly api = inject(BackendApiService);

  async resolve(portfolioId: number, user: User): Promise<PortfolioAccess> {
    void user; // the token BackendApiService attaches identifies the caller
    const { data, error, response } = await this.api.client.POST("/api/v1/portfolios/{id}/access", {
      params: { path: { id: portfolioId } },
    });
    if (response.status === 404) {
      return { role: "viewer", canView: false, canWrite: false, isPublic: false };
    }
    if (error || !data) {
      throw new Error(apiErrorMessage(error, `Portfolio access could not be checked (HTTP ${response.status}).`));
    }
    return data;
  }
}
